import {useIsInitialLocation, useQueryParams} from '@aboutyou/router';
import {useLocalStorage} from '@aboutyou/storage';
import {
  INSTALL_BANNER_HEIGHT,
  quickViewHeightCorrectionStyles,
  useIsomorphicLayoutEffect,
} from '@aboutyou/ui-components';
import {getDateDifference} from '@aboutyou/utils';
import {useCallback, useRef} from 'react';
import {AppInstallBannerStyle} from './HeaderInstallBannerWrapper';

export const APP_INSTALL_BANNER_LAST_CLOSED =
  'app_install_banner_last_closed_tadarida';
export const DISABLE_APP_INSTALL_BANNER = 'disableAppInstallBanner';
export const TEMPORARILY_DISABLE_APP_INSTALL_BANNER = 'aib';

export function useIsBannerOpen() {
  const queryParams = useQueryParams();
  const {close, open, lastClosed} = useOpenCloseHandler();
  const shouldHideTemporarily = useShouldHideAppInstallBannerTemporarily();

  const num = getDateDifference({
    end: new Date(),
    start: new Date(lastClosed!),
    format: 'days',
  });

  const neverClosed = !lastClosed;
  const lastClosedMoreThan15DaysAgo = num >= 15;

  const disabledInCurrentBrowserSession = useRef(
    queryParams.get(DISABLE_APP_INSTALL_BANNER) === '1',
  ).current;

  const isOpen =
    !disabledInCurrentBrowserSession &&
    !shouldHideTemporarily &&
    (neverClosed || lastClosedMoreThan15DaysAgo);

  return {isOpen, close, open, shouldHideTemporarily} as const;
}

const closeWithoutAnimation = () => {
  document.body.style.setProperty(
    AppInstallBannerStyle.bottomOffsetCssVariable,
    AppInstallBannerStyle.bottomOffsetWhenHidden,
  );
  document.body.style.setProperty(
    quickViewHeightCorrectionStyles.quickViewHeightCorrection,
    quickViewHeightCorrectionStyles.heightCorrectionWhenInstallBannerIsClosed,
  );
  document.body.style.removeProperty('transition');
  document.body.style.removeProperty('transform');
};

const openWithoutAnimation = () => {
  document.body.style.setProperty(
    AppInstallBannerStyle.bottomOffsetCssVariable,
    AppInstallBannerStyle.bottomOffsetWhenShown,
  );
  document.body.style.setProperty(
    quickViewHeightCorrectionStyles.quickViewHeightCorrection,
    quickViewHeightCorrectionStyles.heightCorrectionWhenInstallBannerIsOpen,
  );
  document.body.style.removeProperty('transition');
  document.body.style.removeProperty('transform');
};

const closeWithAnimation = () => {
  // if the body is locked, we expect to have dialogs open. When adding the transform in the next step the body will change the stacking context. As the result, this would not show the dialog in most cases any longer. Therefore, in case the body is locked, we disable the animation so that the UI does not flicker in any way.
  if (document.body.classList.contains('lock-body-scroll')) {
    closeWithoutAnimation();
    return;
  }
  document.body.addEventListener('transitionend', closeWithoutAnimation, {
    once: true,
  });
  document.body.style.setProperty('transition', 'transform 0.5s ease-in-out');
  document.body.style.setProperty(
    'transform',
    `translateY(${AppInstallBannerStyle.bottomOffsetWhenHidden})`,
  );
};

const openWithAnimation = () => {
  document.body.addEventListener('transitionend', openWithoutAnimation, {
    once: true,
  });

  document.body.style.setProperty('transition', 'transform 0.5s ease-in-out');
  document.body.style.setProperty(
    'transform',
    `translateY(${INSTALL_BANNER_HEIGHT}px)`,
  );
};

const useOpenCloseHandler = () => {
  const [lastClosed, setLastClosed] = useLocalStorage({
    // This has to be different from the legacy key because we force close the legacy
    // version in favor of the new banner. See the effect code above.
    key: APP_INSTALL_BANNER_LAST_CLOSED,
    defaultValue: '',
    // In this particular case, using a slightly different timestamp on both server and client
    // should not be a problem as the header would only re-open after 15 days of dismissing
    // and SSR only takes a few hundred milliseconds at max.
    fallbackValue: new Date().toISOString(),
  });

  const close = useCallback(
    (options?: {disableAnimation?: boolean; setLastTimeClosed?: boolean}) => {
      if (options?.setLastTimeClosed) {
        setLastClosed(new Date().toISOString());
      }
      options?.disableAnimation
        ? closeWithoutAnimation()
        : closeWithAnimation();
    },
    [setLastClosed],
  );

  const open = (options?: {disableAnimation?: boolean}) => {
    options?.disableAnimation ? openWithoutAnimation() : openWithAnimation();
  };

  return {
    close,
    open,
    lastClosed,
  };
};

const useShouldHideAppInstallBannerTemporarily = () => {
  const {open} = useOpenCloseHandler();
  const queryParams = useQueryParams();
  const isInitialLocation = useIsInitialLocation();
  const hiddenByAIBQueryParam = queryParams.get(
    TEMPORARILY_DISABLE_APP_INSTALL_BANNER,
  );
  const hiddenByQueryParam = hiddenByAIBQueryParam === '0';
  const shouldBeTemporarilyHidden = isInitialLocation && hiddenByQueryParam;
  const shouldOpen = useRef(shouldBeTemporarilyHidden);

  useIsomorphicLayoutEffect(() => {
    if (shouldOpen.current) {
      open({
        disableAnimation: true,
      });
      shouldOpen.current = false;
    }
  }, [shouldOpen.current]);

  return shouldBeTemporarilyHidden;
};
